import type { RunBreakdown, RunSummary } from '../types';

/**
 * Legend order, words and colour. Left to right: what's finished, then what
 * wants a person, then what's moving, then what isn't — the order the card's
 * own linked-workflow bar uses (`TaskCard`), with the two states it folds into
 * `in_progress` shown apart. Colours are the board's state tokens, spelled out
 * so Tailwind's scanner finds them.
 */
const BUCKETS: {
  key: keyof RunBreakdown;
  label: string;
  className: string;
}[] = [
  { key: 'done', label: 'Done', className: 'bg-board-state-done' },
  {
    key: 'needsAttention',
    label: 'Needs attention',
    className: 'bg-board-state-failed',
  },
  {
    key: 'needsReview',
    label: 'Needs review',
    className: 'bg-board-state-waiting',
  },
  { key: 'running', label: 'Running', className: 'bg-board-state-running' },
  { key: 'idle', label: 'Not running', className: 'bg-muted-foreground/40' },
];

export interface RunProgressProps {
  summary: RunSummary;
}

/**
 * How far the run has got, in words, as a bar, and as a legend of where the
 * rest stands.
 *
 * The bar fills with finished work only. A bar is read as "how much is
 * done", and filling it with review and running segments made a run with
 * nothing done look complete at a glance. The other states are counted in the
 * legend, which names only buckets that hold a task — five entries of which
 * four read "0" said less than the one that did not.
 *
 * No per-step counts: the board or list below shows where each task stands,
 * and the callout under this (`RunStatusBanner`) names the ones that need
 * someone.
 */
export function RunProgress({ summary }: RunProgressProps) {
  const { done, total, breakdown } = summary;
  const present = BUCKETS.filter((bucket) => breakdown[bucket.key] > 0);
  const percent = total > 0 ? (done / total) * 100 : 0;

  return (
    <div
      role="region"
      aria-label="Run progress"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm"
    >
      <span className="text-foreground font-medium tabular-nums">
        {done}/{total} done
      </span>

      <span
        role="progressbar"
        aria-label="Tasks done"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        data-testid="run-progress-bar"
        className="bg-muted flex h-2 min-w-40 flex-1 overflow-hidden rounded-full"
      >
        <span
          className="bg-board-state-done block h-full transition-[width]"
          style={{ width: `${String(percent)}%` }}
        />
      </span>

      <ul className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {present.map((bucket) => (
          <li key={bucket.key} className="flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className={`${bucket.className} size-2 shrink-0 rounded-full`}
            />
            {bucket.label}
            <span className="text-foreground tabular-nums">
              {breakdown[bucket.key]}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
